import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { PublicShell } from "@/components/PublicShell";
import { BriefDownloadGate } from "@/components/BriefDownloadGate";
import { Seo } from "@/components/Seo";
import { FileText, Presentation, Download, Eye, ArrowRight, Loader2 } from "lucide-react";

const ICON = { "sales-deck": Presentation };

export default function Brief() {
  const [docs, setDocs] = useState(null);
  const [gate, setGate] = useState(null); // { slug, name } awaiting capture
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    api.get("/public/brief").then((r) => setDocs(r.data.docs || [])).catch(() => setDocs([]));
    try { setUnlocked(localStorage.getItem("erio_brief_unlocked") === "1"); } catch { /* ignore */ }
  }, []);

  const fmtSize = (b) => (!b ? "" : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

  const triggerDownload = (slug) => {
    const a = document.createElement("a");
    a.href = `${API}/public/brief/${slug}?download=1`;
    a.rel = "noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const requestDownload = (doc) => {
    if (unlocked) triggerDownload(doc.slug);
    else setGate({ slug: doc.slug, name: doc.name });
  };

  return (
    <PublicShell active="/brief">
      <Seo title="ERIO White Papers, Executive Brief & Sales Deck — EKSELENS" path="/brief"
        description="Download the EKSELENS ERIO executive brief, white paper and board sales deck: quantify, predict and prove enterprise risk." />
      <section className="border-b border-black/5 bg-white">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-ek-purple">Resources</span>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">The thinking behind ERIO</h1>
          <p className="mt-4 max-w-2xl text-base text-ek-ink/70">
            The executive brief, the full white paper and the board presentation — why traditional risk
            management is failing, and how ERIO quantifies, predicts and proves enterprise, supply-chain and
            geopolitical risk.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-14">
        {docs === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-ek-purple" /></div>
        ) : docs.length === 0 ? (
          <p className="py-16 text-center text-ek-ink/70" data-testid="brief-empty">Documents are being prepared — check back shortly.</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-3" data-testid="brief-list">
            {docs.map((d) => {
              const Icon = ICON[d.slug] || FileText;
              return (
                <div key={d.slug} className="flex flex-col rounded-2xl border border-black/5 bg-white p-7 shadow-sm transition-shadow hover:shadow-md"
                  data-testid={`brief-card-${d.slug}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-ek-purple/10 text-ek-purple">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold">{d.label}</h3>
                  <p className="mt-2 flex-1 text-sm text-ek-ink/65">{d.blurb}</p>
                  <div className="mt-5 flex items-center gap-2">
                    <button onClick={() => requestDownload(d)}
                      className="ek-btn flex items-center gap-2 px-4 py-2 text-sm font-semibold"
                      data-testid={`brief-download-${d.slug}`}>
                      <Download size={15} /> Download
                    </button>
                    <a href={`${API}/public/brief/${d.slug}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-ek-ink/70 hover:text-ek-purple"
                      data-testid={`brief-view-${d.slug}`}>
                      <Eye size={15} /> Read online
                    </a>
                    <span className="ml-auto text-xs text-ek-ink/65">{fmtSize(d.size)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-12 flex flex-col items-center justify-between gap-4 rounded-2xl bg-ek-ink px-8 py-7 text-white sm:flex-row">
          <div>
            <p className="text-lg font-semibold">Want to see it on your own data?</p>
            <p className="mt-1 text-sm text-white/60">Walk a live DORA-aligned tenant or scope a design-partner pilot.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/demo" className="flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-ek-ink hover:bg-white/90" data-testid="brief-cta-demo">
              Book a demo <ArrowRight size={15} />
            </Link>
            <Link to="/pilot" className="flex items-center gap-2 rounded-lg border border-white/30 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10" data-testid="brief-cta-pilot">
              Book a pilot
            </Link>
            <Link to="/maturity-assessment" className="flex items-center gap-2 rounded-lg border border-white/30 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10" data-testid="brief-cta-maturity">
              Take the PMO maturity assessment
            </Link>
          </div>
        </div>
      </section>

      {gate && (
        <BriefDownloadGate slug={gate.slug} docName={gate.name}
          onClose={() => setGate(null)}
          onUnlocked={() => { setUnlocked(true); const s = gate.slug; setGate(null); triggerDownload(s); }} />
      )}
    </PublicShell>
  );
}
